import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid';
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter
} from 'react-table';
import { Icon, Grid, Table, Pagination } from 'semantic-ui-react';
import { SortIcon, TotalPill } from '../../';
import { PageSizeField } from '../';
import { PAGE_SIZE_OPTIONS } from '../../../constants';

const defaultSort = { id: 'ProductBrand.Name', desc: false };

const Desktop = props => {
  const { data, total, searchFilter, placeholder } = props;
  const columns = React.useMemo(() => props.columns, []);

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    rows, // rows to get filtered total number

    // The rest of these things are super handy, too ;)
    pageCount,
    gotoPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE_OPTIONS[0],
        sortBy: [defaultSort]
      },
      disableSortRemove: true
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // when searchFilter changes, trigger `setGlobalFilter`
  // filter is moved to keep the selected sorting
  useEffect(() => {
    setGlobalFilter(searchFilter);
  }, [searchFilter, data]);

  const renderTable = () => (
    <Table
      striped
      selectable
      {...getTableProps()}
      data-test="asset-download-table"
    >
      <Table.Header key="table-header">
        {headerGroups.map(headerGroup => (
          <Table.Row {...headerGroup.getHeaderGroupProps()} key="table-row">
            {headerGroup.headers.map(column => (
              <Table.HeaderCell
                {...column.getHeaderProps(() => column.getSortByToggleProps())}
                key={column.id}
              >
                <SortIcon
                  active={column.isSorted}
                  isDesc={column.isSorted && column.isSortedDesc}
                >
                  {column.render('Header')}
                </SortIcon>
              </Table.HeaderCell>
            ))}
          </Table.Row>
        ))}
      </Table.Header>

      <Table.Body
        {...getTableBodyProps()}
        data-test="asset-download-table-tbody"
      >
        {page.map(row => {
          prepareRow(row);
          return (
            <Table.Row {...row.getRowProps()} key={uuidv4()}>
              {row.cells.map(cell => {
                return (
                  <Table.Cell {...cell.getCellProps()} key={uuidv4()}>
                    {cell.render('Cell')}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          );
        })}
      </Table.Body>

      {data.length > PAGE_SIZE_OPTIONS[0] && (
        <Table.Footer data-testid="downloadable-assets-results-footer">
          <Table.Row>
            <Table.Cell colSpan={columns && columns.length}>
              <Grid className="grid--spreaded">
                <Grid.Column />
                <Pagination
                  activePage={pageIndex + 1}
                  onPageChange={(e, { activePage }) => {
                    gotoPage(activePage - 1);
                  }}
                  totalPages={pageCount}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{
                    content: <Icon name="angle left" />,
                    icon: true,
                    disabled: pageIndex + 1 === 1
                  }}
                  nextItem={{
                    content: <Icon name="angle right" />,
                    icon: true,
                    disabled: pageIndex + 1 === pageCount
                  }}
                  secondary
                />
                <PageSizeField
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                  }}
                />
              </Grid>
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );

  // rows holds filtered data from `data` array
  const count = pageCount > 1 ? rows.length : page.length;
  return (
    <Fragment>
      {data && count > 0 && total !== count && (
        <TotalPill
          total={count}
          unit="Result"
          trail={<small>(out of {total})</small>}
        />
      )}
      {count > 0 ? renderTable() : placeholder}
    </Fragment>
  );
};

Desktop.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      sortType: PropTypes.string,
      Cell: PropTypes.func
    })
  )
};

export default Desktop;



// WEBPACK FOOTER //
// ./src/components/AssetDownloadsTable/body/Desktop.js